import React from 'react';
import { X, Info } from 'lucide-react';
import { tuitionFeeData } from '@/constants/tuitionFeeData';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';

interface TuitionFeeReferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GROUPS = [
  { key: 'primary' as const, label: 'Primary School', rateClass: 'text-accent-700', rowBg: 'bg-accent-50/60' },
  { key: 'secondary' as const, label: 'Secondary School', rateClass: 'text-secondary', rowBg: 'bg-secondary-50/60' },
  { key: 'pre-university' as const, label: 'Pre-University', rateClass: 'text-primary', rowBg: 'bg-primary-50/60' },
];

const TuitionFeeReferenceModal: React.FC<TuitionFeeReferenceModalProps> = ({ isOpen, onClose }) => {
  const { locale } = useOptionalI18n();
  const isZh = locale === 'zh-CN';
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900 text-base">{isZh ? '补习收费参考' : 'Tuition Rate Guide'}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{isZh ? '市场常见收费 · 每小时 RM' : 'Standard market rates · RM per hour'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            aria-label={isZh ? '关闭' : 'Close'}
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-y-auto px-5 py-4">
          <div className="rounded-xl overflow-hidden border border-gray-200">
          <table className="w-full">
            <thead>
              <tr className="bg-primary text-white">
                <th className="px-5 py-3 text-left text-xs font-semibold">{isZh ? '教育阶段' : 'Education Level'}</th>
                <th className="px-5 py-3 text-right text-xs font-semibold">{isZh ? '平均收费 (RM/小时)' : 'Avg. Rate (RM/hr)'}</th>
              </tr>
            </thead>
            <tbody>
              {GROUPS.map((group) => {
                const items = tuitionFeeData.filter((item) => item.group === group.key);
                if (!items.length) return null;
                return (
                  <React.Fragment key={group.key}>
                    <tr className={`${group.rowBg} border-t border-gray-200`}>
                      <td colSpan={2} className="px-5 py-3">
                        <p className={`text-sm font-bold ${group.rateClass}`}>{localizeText(locale, group.label)}</p>
                      </td>
                    </tr>
                    {items.map((item, i) => (
                      <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3 pl-7 text-sm text-gray-700">{localizeText(locale, item.level)}</td>
                        <td className={`px-5 py-3 text-sm font-bold text-right ${group.rateClass}`}>{item.averageRate}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100">
          <div className="flex gap-2 bg-primary-50 rounded-xl p-3">
            <Info className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-primary-700 leading-relaxed">
              {isZh ? '以上收费仅供参考，实际收费会因导师经验、资历与地点而有所不同。' : 'Rates are indicative only. Actual rates may vary based on tutor experience, qualifications, and location.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TuitionFeeReferenceModal;
